/**
 * useExpenseQueue — IDB-backed offline-first queue for /api/associates/expense-logs/.
 *
 * Mirrors the visit-lifecycle queue pattern (#1471) but for the expenses
 * domain. The Expenses page submits through this hook; the hook persists
 * to IDB, attempts an immediate POST, and retries on `online` /
 * `visibilitychange` events.
 *
 * Why a dedicated queue (not the central useSyncEngine):
 *   - Expense submits are multipart (receipt as Blob) — the central engine
 *     only handles JSON payloads
 *   - Receipt blobs need to live in IDB until acknowledged, so they
 *     survive reload + offline scenarios
 *
 * The Expenses page calls:
 *   - enqueueExpense({...}) on submit (returns the optimistic UUID)
 *   - drainQueue() implicitly via the auto-mounted listener
 *   - deleteQueueEntry(uuid) for cancel-while-pending
 *   - useExpenseQueue() for the React-visible pendingEntries list + status
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError, expensesApi } from '../services/api';
import {
  type ExpenseQueueRecord,
  deleteExpenseQueueEntry,
  getAllExpenseQueueEntries,
  getPendingExpenseEntries,
  putExpenseQueueEntry,
} from '../utils/expensesDB';

function generateId(): string {
  return `exp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const RETRY_THROTTLE_MS = 8_000;

export interface NewExpenseSubmission {
  expenseTypeId: number;
  expenseTypeName: string;
  expenseDate: string; // YYYY-MM-DD
  amount: number;
  description?: string;
  receiptBlob?: Blob;
  receiptFilename?: string;
  receiptMimeType?: string;
}

export interface DrainResult {
  attempted: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

/**
 * Enqueue an expense for sync. Returns the entry's UUID so the page can
 * render an optimistic row immediately. Does NOT wait for the network attempt.
 */
export async function enqueueExpense(submission: NewExpenseSubmission): Promise<string> {
  const id = generateId();
  const record: ExpenseQueueRecord = {
    id,
    expenseTypeId: submission.expenseTypeId,
    expenseTypeName: submission.expenseTypeName,
    expenseDate: submission.expenseDate,
    amount: submission.amount,
    description: submission.description,
    receiptBlob: submission.receiptBlob,
    receiptFilename: submission.receiptFilename,
    receiptMimeType: submission.receiptMimeType,
    syncStatus: 'pending',
    syncAttempts: 0,
    createdAt: new Date().toISOString(),
  };
  await putExpenseQueueEntry(record);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('rdm:expense-enqueued'));
  }
  return id;
}

// Module-level lock so simultaneous callers (e.g. submit fires both an
// `rdm:expense-enqueued` event AND directly calls drainNow) don't both
// POST the same entry. The pendingEntries.read + status='syncing'.write
// is not atomic across IDB transactions, so a soft singleton guard here
// is the simplest correct fix.
let drainInFlight = false;

/**
 * Process all pending expense entries. The module-level guard ensures only
 * one drain runs at a time across the whole app.
 */
export async function drainExpenseQueue(): Promise<DrainResult> {
  const result: DrainResult = { attempted: 0, succeeded: 0, failed: 0, skipped: 0 };
  if (drainInFlight) return result;
  drainInFlight = true;
  try {
    return await drainExpenseQueueInner();
  } finally {
    drainInFlight = false;
  }
}

async function drainExpenseQueueInner(): Promise<DrainResult> {
  const result: DrainResult = { attempted: 0, succeeded: 0, failed: 0, skipped: 0 };
  const pending = await getPendingExpenseEntries();
  pending.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const now = Date.now();

  for (const entry of pending) {
    if (entry.lastSyncAttempt) {
      const lastMs = new Date(entry.lastSyncAttempt).getTime();
      if (now - lastMs < RETRY_THROTTLE_MS) {
        result.skipped += 1;
        continue;
      }
    }
    result.attempted += 1;

    // Mark syncing first to prevent a concurrent drain from grabbing the
    // same entry. We restore to 'pending' on transient failures below.
    const syncing: ExpenseQueueRecord = {
      ...entry,
      syncStatus: 'syncing',
      lastSyncAttempt: new Date().toISOString(),
    };
    await putExpenseQueueEntry(syncing);

    try {
      let receiptFile: File | undefined;
      if (entry.receiptBlob && entry.receiptFilename) {
        receiptFile = new File([entry.receiptBlob], entry.receiptFilename, {
          type: entry.receiptMimeType ?? entry.receiptBlob.type ?? 'application/octet-stream',
        });
      }
      await expensesApi.createExpenseLog({
        expenseId: entry.expenseTypeId,
        date: entry.expenseDate,
        amount: entry.amount,
        description: entry.description?.trim() || undefined,
        document: receiptFile,
      });
      // Success — drop the queue entry. Caller should re-fetch the server
      // list to pick up the canonical record.
      await deleteExpenseQueueEntry(entry.id);
      result.succeeded += 1;
    } catch (err) {
      const isPermanent = err instanceof ApiError && err.status >= 400 && err.status < 500;
      const message =
        err instanceof ApiError
          ? `HTTP ${err.status}${err.message ? `: ${err.message}` : ''}`
          : err instanceof Error
          ? err.message
          : String(err);

      const updated: ExpenseQueueRecord = {
        ...syncing,
        syncStatus: isPermanent ? 'failed' : 'pending',
        syncAttempts: entry.syncAttempts + 1,
        syncError: message,
      };
      await putExpenseQueueEntry(updated);
      if (isPermanent) {
        result.failed += 1;
      } else {
        result.skipped += 1; // will retry on next drain
      }
    }
  }

  if (typeof window !== 'undefined' && (result.succeeded > 0 || result.failed > 0)) {
    window.dispatchEvent(new Event('rdm:expense-queue-changed'));
  }
  return result;
}

export async function deleteQueueEntry(id: string): Promise<void> {
  await deleteExpenseQueueEntry(id);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('rdm:expense-queue-changed'));
  }
}

export interface UseExpenseQueueResult {
  /** All entries — pending + syncing + failed — in createdAt order. */
  queueEntries: ExpenseQueueRecord[];
  pendingCount: number;
  failedCount: number;
  isDraining: boolean;
  drainNow: () => Promise<DrainResult>;
  refresh: () => Promise<void>;
}

/**
 * React hook — exposes the queue state for the Expenses page to merge with
 * server-side logs. Auto-drains on mount, on `online`, on `visibilitychange`,
 * on `rdm:expense-enqueued`, and on a 60s background tick.
 */
export function useExpenseQueue(): UseExpenseQueueResult {
  const [queueEntries, setQueueEntries] = useState<ExpenseQueueRecord[]>([]);
  const [isDraining, setIsDraining] = useState(false);

  const refresh = useCallback(async () => {
    const all = await getAllExpenseQueueEntries();
    all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    setQueueEntries(all);
  }, []);

  const drainNow = useCallback(async (): Promise<DrainResult> => {
    setIsDraining(true);
    try {
      const r = await drainExpenseQueue();
      await refresh();
      return r;
    } finally {
      setIsDraining(false);
    }
  }, [refresh]);

  useEffect(() => {
    refresh().catch((e) => console.warn('[useExpenseQueue] initial refresh failed', e));
  }, [refresh]);

  useEffect(() => {
    const onChange = () => {
      void refresh();
    };
    const onOnlineOrVisible = () => {
      void drainNow();
    };
    window.addEventListener('rdm:expense-enqueued', onOnlineOrVisible);
    window.addEventListener('rdm:expense-queue-changed', onChange);
    window.addEventListener('online', onOnlineOrVisible);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') onOnlineOrVisible();
    });
    // Initial drain on mount — pick up anything left from prior session
    void drainNow();
    // Background tick
    const tick = setInterval(() => {
      if (navigator.onLine && document.visibilityState === 'visible') void drainNow();
    }, 60_000);
    return () => {
      window.removeEventListener('rdm:expense-enqueued', onOnlineOrVisible);
      window.removeEventListener('rdm:expense-queue-changed', onChange);
      window.removeEventListener('online', onOnlineOrVisible);
      clearInterval(tick);
    };
  }, [refresh, drainNow]);

  const pendingCount = queueEntries.filter((e) => e.syncStatus === 'pending' || e.syncStatus === 'syncing').length;
  const failedCount = queueEntries.filter((e) => e.syncStatus === 'failed').length;

  return { queueEntries, pendingCount, failedCount, isDraining, drainNow, refresh };
}
